import React, { useMemo, useState } from "react";

export default function AuthSignup({
  mode = "signup",
  onModeChange,
  onSignup,
  onLogin,
  onForgotPassword,
  onResetPassword,
  resetToken,
  resetUserId,
  busy,
  error,
}) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    crNumber: "",
    mt5Number: "",
  });
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [resetSubmitted, setResetSubmitted] = useState(false);

  function updateField(field) {
    return (event) => {
      setForm((currentForm) => ({
        ...currentForm,
        [field]: event.target.value,
      }));
    };
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (mode === "login") {
      onLogin(form);
      return;
    }

    if (mode === "forgot") {
      onForgotPassword({ email: form.email });
      setForgotSubmitted(true);
      return;
    }

    if (mode === "reset") {
      onResetPassword({ token: resetToken, userId: resetUserId, password: form.password });
      setResetSubmitted(true);
      return;
    }

    onSignup(form);
  }

  const hasAccountId = form.crNumber.trim() || form.mt5Number.trim();

  const title = useMemo(() => {
    if (mode === "login") return "Welcome back to Spike";
    if (mode === "forgot") return "Forgot your password?";
    if (mode === "reset") return "Set a new password";
    return "Create your Spike account";
  }, [mode]);

  const copy = useMemo(() => {
    if (mode === "login") return "Enter your credentials to access your dashboard. You can log in with your email or one of your account numbers.";
    if (mode === "forgot") return "Enter your registered email address and we'll send you a secure link to reset your password.";
    if (mode === "reset") return "Choose a strong password of at least 6 characters for your Spike account.";
    return "Sign up to access real-time market zones, alerts, and trading tools. At least one account number is required to register.";
  }, [mode]);

  if (mode === "forgot" && forgotSubmitted && !error) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-kicker">Password Reset</div>
          <h1 className="auth-title">Check your email</h1>
          <p className="auth-copy">
            If that email is registered, a reset link has been sent. Check your inbox and follow the link to set a new password.
          </p>
          <button
            type="button"
            className="auth-token-button"
            onClick={() => {
              setForgotSubmitted(false);
              setForm((f) => ({ ...f, email: "" }));
              onModeChange?.("login");
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (mode === "reset" && resetSubmitted && !error) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-kicker">Password Reset</div>
          <h1 className="auth-title">Password updated</h1>
          <p className="auth-copy">Your password has been reset successfully. You can now log in with your new password.</p>
          <button
            type="button"
            className="auth-token-button"
            onClick={() => onModeChange?.("login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        {mode !== "forgot" && mode !== "reset" ? (
          <div className="auth-switch">
            <button
              type="button"
              className={`auth-switch-button${mode === "signup" ? " auth-switch-button-active" : ""}`}
              onClick={() => onModeChange?.("signup")}
              disabled={busy}
            >
              Sign Up
            </button>
            <button
              type="button"
              className={`auth-switch-button${mode === "login" ? " auth-switch-button-active" : ""}`}
              onClick={() => onModeChange?.("login")}
              disabled={busy}
            >
              Log In
            </button>
          </div>
        ) : null}

        <div className="auth-kicker">
          {mode === "login" ? "Welcome Back" : mode === "forgot" ? "Password Reset" : mode === "reset" ? "New Password" : "Get Started"}
        </div>
        <h1 className="auth-title">{title}</h1>
        <p className="auth-copy">{copy}</p>

        <form className="auth-form" onSubmit={handleSubmit}>
          {mode === "signup" ? (
            <>
              <label className="auth-label" htmlFor="full-name">
                Full Name
              </label>
              <input
                id="full-name"
                className="auth-input"
                type="text"
                value={form.fullName}
                onChange={updateField("fullName")}
                placeholder="Enter your full name"
                disabled={busy}
                required
              />
            </>
          ) : null}

          {mode === "forgot" ? (
            <>
              <label className="auth-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className="auth-input"
                type="email"
                value={form.email}
                onChange={updateField("email")}
                placeholder="Enter your email"
                disabled={busy}
                required
              />
            </>
          ) : null}

          {mode === "reset" ? (
            <>
              <label className="auth-label" htmlFor="password">
                New Password
              </label>
              <input
                id="password"
                className="auth-input"
                type="password"
                value={form.password}
                onChange={updateField("password")}
                placeholder="Enter new password"
                disabled={busy}
                minLength={6}
                required
              />

              <label className="auth-label" htmlFor="confirm-password">
                Confirm Password
              </label>
              <input
                id="confirm-password"
                className="auth-input"
                type="password"
                value={form.confirmPassword}
                onChange={updateField("confirmPassword")}
                placeholder="Confirm new password"
                disabled={busy}
                minLength={6}
                required
              />
            </>
          ) : null}

          {mode !== "forgot" && mode !== "reset" ? (
            <>
              <label className="auth-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className="auth-input"
                type="email"
                value={form.email}
                onChange={updateField("email")}
                placeholder="Enter your email"
                disabled={busy}
              />

              <label className="auth-label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                className="auth-input"
                type="password"
                value={form.password}
                onChange={updateField("password")}
                placeholder={mode === "login" ? "Enter your password" : "Create a password"}
                disabled={busy}
                minLength={6}
                required
              />

              <div className="auth-inline-copy">
                {mode === "login"
                  ? "Or log in with one of your account numbers below."
                  : "Enter at least one of your trading account numbers below."}
              </div>

              <label className="auth-label" htmlFor="cr-number">
                CR Number
              </label>
              <input
                id="cr-number"
                className="auth-input"
                type="text"
                value={form.crNumber}
                onChange={updateField("crNumber")}
                placeholder="Enter CR Number"
                disabled={busy}
              />

              <label className="auth-label" htmlFor="mt5-number">
                MT5 Number
              </label>
              <input
                id="mt5-number"
                className="auth-input"
                type="text"
                value={form.mt5Number}
                onChange={updateField("mt5Number")}
                placeholder="Enter MT5 Number"
                disabled={busy}
              />
            </>
          ) : null}

          {mode === "login" ? (
            <button
              type="button"
              className="auth-forgot-link"
              onClick={() => onModeChange?.("forgot")}
              disabled={busy}
            >
              Forgot password?
            </button>
          ) : null}

          {mode === "reset" && form.password && form.confirmPassword && form.password !== form.confirmPassword ? (
            <p className="auth-error">Passwords do not match.</p>
          ) : null}

          <button
            type="submit"
            className="auth-token-button"
            disabled={
              busy ||
              (mode === "signup" && (!form.password.trim() || !form.fullName.trim() || !hasAccountId)) ||
              (mode === "login" && (!form.password.trim() || (!form.email.trim() && !form.crNumber.trim() && !form.mt5Number.trim()))) ||
              (mode === "forgot" && !form.email.trim()) ||
              (mode === "reset" && (!form.password.trim() || !form.confirmPassword.trim() || form.password !== form.confirmPassword))
            }
          >
            {busy
              ? mode === "login"
                ? "Logging in..."
                : mode === "forgot"
                ? "Sending..."
                : mode === "reset"
                ? "Resetting..."
                : "Creating Account..."
              : mode === "login"
              ? "Log In"
              : mode === "forgot"
              ? "Send Reset Link"
              : mode === "reset"
              ? "Reset Password"
              : "Create Account"}
          </button>
        </form>

        {error ? <p className="auth-error">{error}</p> : null}

        {mode === "forgot" ? (
          <p className="auth-footnote">
            Remember your password?{" "}
            <button
              type="button"
              className="auth-forgot-link"
              onClick={() => onModeChange?.("login")}
              disabled={busy}
            >
              Back to Log In
            </button>
          </p>
        ) : mode === "reset" ? null : (
          <p className="auth-footnote">
            {mode === "login"
              ? "Don't have an account? Switch to Sign Up above."
              : "Your CR Number or MT5 Number is required to verify your trading account."}
          </p>
        )}
      </div>
    </div>
  );
}
